import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useUrlState — React state mirrored into a URL search param (replace: true), so
 * the selected tab / project / filter survives refresh, deep links and back/forward.
 * Nested tabs coexist by using different param names: ?tab=procurement&sub=completed&project=…
 *
 *   const [tab, setTab] = useUrlTab('tab', 'grn');
 *   const [projectId, setProjectId] = useUrlState('project', '');
 */
export function useUrlState(key, defaultValue = '', { replace = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = raw === null ? defaultValue : raw;

  const setValue = useCallback((next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      const resolved = typeof next === 'function' ? next(p.get(key) ?? defaultValue) : next;
      if (resolved === null || resolved === undefined || resolved === '' || resolved === defaultValue) p.delete(key);
      else p.set(key, String(resolved));
      return p;
    }, { replace });
  }, [key, defaultValue, replace, setSearchParams]);

  return [value, setValue];
}

// Tab switches push a history entry so Back returns to the previous tab; filters (project, search) replace.
export const useUrlTab = (key = 'tab', defaultValue = '') => useUrlState(key, defaultValue, { replace: false });

export default useUrlState;
